import React, { useEffect, useState } from "react";
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Drawer, Typography } from "@mui/material";
import {
  ML_CONFIDENCE_KEY,
  MLfilterKeys,
  cn,
  customEnumLabelMap,
  customRangeFilters,
  filterKeys,
  getMinMaxOfData,
  getMinMaxOfMeasureMentData,
  getRatioMinMaxOfData,
  milkyValues,
  negativeFiltersList,
  negativeKeyMap,
} from "../../utils/utils";
import _ from "lodash";
import FilterMinMaxNew from "../filterMinMaxNew";
import TableFilterTop from "../TableFilter/TableFilterTop";
import amplitude from "../../utils/ampli";
import TableFilter2 from "../TableFilter/tableFilter2";
import { pricing, setFilteredRedux } from "../../redux/slices/pricingSlice";
import { useDispatch, useSelector } from "react-redux";
import HalfExpandedCard from "./halfExpandedCard";
import SaveSearchDropdown from "./saveSearchDropdown";
import FilterMinMaxMemo from "../filterMinMaxMemo";

const TopFilterDrawer = ({ open, onClose, data, filtered, setFiltered, sortState }) => {
  // local state.
  const [filterState, setFilterState] = useState([]);
  const preDefinedSearchId = useSelector(pricing).preDefinedSearchId;
  const dispatch = useDispatch();
  const enumLabelMap = customEnumLabelMap;
  const customRangeDefaultsMap = Object.fromEntries(
    customRangeFilters.map((range) => [range.key, range])
  );
  const customFiltersRaw = useSelector(pricing)?.config?.custom_filters ?? [];
  const customFiltersRawByKey = Object.fromEntries(customFiltersRaw.map(f => [f.key, f]));
  const pricingActionTags = useSelector(pricing)?.config?.pricing_action_tags ?? [];
  filterKeys.pricing_action_tags = pricingActionTags.map((t) => t.tag);

  const normalizeEnum = f => ({
      id: f.key,
      label: enumLabelMap[f.key] ?? f.label,       // your label wins
      type: "enum",
      options: f.options ?? [],
      countsMap: Object.fromEntries((f.options_with_count || []).map(o => [o.value])),
  });

  const normalizeRange = (f) => {
    const fallback = customRangeDefaultsMap[f.key];
    const min = fallback?.min ?? f.from ?? 0;
    const max = fallback?.max ?? f.to ?? 999;

    return {
      id: f.key,
      label: fallback?.label ?? f.label,
      type: "range",
      from: min,
      to: max,
    };
  };




  const ranges = customRangeFilters
    .map(({ key }) => customFiltersRawByKey[key])
    .filter(Boolean)
    .map(normalizeRange);

  const BUCKET_KEY = "stock_performance_data.bucket";

  const enums = Object.keys(enumLabelMap)
    .filter(key => key !== BUCKET_KEY)
    .map(key => customFiltersRawByKey[key])
    .filter(Boolean)
    .map(normalizeEnum);

  const bucketFilter = customFiltersRawByKey[BUCKET_KEY]
    ? normalizeEnum(customFiltersRawByKey[BUCKET_KEY])
    : null;

  const ordered = [...ranges, ...enums];
  const mlConfidenceOptions = MLfilterKeys[ML_CONFIDENCE_KEY] ?? [];
  
  const getFiltered = (fObj) =>
    filterState.find(x => x.id === fObj.id) ||
    (fObj.type === "range"
      ? { id: fObj.id, value: { min: fObj.from, max: fObj.to } }
      : { id: fObj.id, value: [] });
  // we set the filter state to the filtered value, when the drawer is opened.
  // this is done so that the drawer shows the filtered values.
  useEffect(() => {
    if (open) {
      setFilterState(filtered);
    }
  }, [open, filtered, dispatch]);


  const mFilters = [
    {
      label: "Carat",
      key: "Carat",
      min: 0,
      max: 100,
    },
    {
      label: "Length",
      key: "Length",
      min: 0,
      max: 100,
    },
    {
      label: "Width",
      key: "Width",
      min: 0,
      max: 100,
    },
    {
      label: "Depth",
      key: "Depth",
      min: 0,
      max: 100,
    },
    {
      label: "Ratio",
      key: "Ratio",
      min: 0,
      max: 100,
    },
  ];

  const mMFilters = [
    {
      label: "Table%",
      key: "Table%",
      min: 0,
      max: 100,
    },
    {
      label: "Depth%",
      key: "Depth%",
      min: 0,
      max: 100,
    },
  ];
  
  const newMFilters = [
    {
      label: "Rank",
      key: "ld_analysis_hash.rap_rank",
      min: 0,
      max: 9999,
    },
    {
      label: "Count",
      key: "ld_analysis_hash.rap_matching_count",
      min: 0,
      max: 9999,
    },
    {
      label: "Rank Percentile",
      key: "rap_rank_percentile",
      min: 0,
      max: 100,
    },

    {
      label: "Cert Aging",
      key: "attr_types.diamond#report_date",
      min: 0,
      max: 9999,
    },
    {
      label: "Rank change",
      key: "change_in_rap_rank",
      min: -9999,
      max: 9999,
    },
    {
      label: "Rank% Change",
      key: "rank_pct_change",
      min: -9999,
      max: 9999,
    },
    {
      label: "Count change",
      key: "change_in_rap_count",
      min: -9999,
      max: 9999,
    },
    {
      label: "Added",
      key: "market_items_added_array_count",
      min: 0,
      max: 9999,
    },
    {
      label: "Sold",
      key: "market_items_sold_array_count",
      min: 0,
      max: 9999,
    },
    {
      label: "Aging",
      key: "attr_types.for_sale_from",
      min: 0,
      max: 9999,
    },
    {
      label: "Broad Count",
      key: "total_saved_prmi",
      min: 0,
      max: 9999,
    },
    {
      label: "My Stock",
      key: "inventory_items_count",
      min: 0,
      max: 9999,
    },
    {
      label: "My Sales",
      key: "sold_items_count",
      min: 0,
      max: 9999,
    },
    {
      label: "My YTD Sales",
      key: "ytd_sales_count",
      min: 0,
      max: 9999,
    },
      {
      label: "Last Repriced Days",
      key: "last_ld_repriced_date",
      min: 0,
      max: 9999,
    },
    {
      label: "Added (30 D)",
      key: "last_n_days_added_array_count",
      min: 0,
      max: 9999,
    },
    {
      label: "Sold (30 D)",
      key: "last_n_days_sold_array_count",
      min: 0,
      max: 9999,
    },
    {
      label: "Rank Change (30 D)",
      key: "last_n_days_rank_change",
      min: -9999,
      max: 9999,
    },
    {
      label: "Count Change (30 D)",
      key: "last_n_days_count_change",
      min: -9999,
      max: 9999,
    },
    {
      label: "Turn",
      key: "stock_performance_data.turn",
      min: 0,
      max: 999,
    },
    {
      label: "Proj Turn",
      key: "stock_performance_data.current_turn",
      min: 0,
      max: 999,
    },
    {
      label: "Overhang",
      key: "stock_performance_data.overhang",
      min: 0,
      max: 999,
    },
    {
      label: "Needs",
      key: "stock_performance_data.needs",
      min: -9999,
      max: 9999,
    },
    {
      label: "Grouped Needs",
      key: "stock_performance_data.grouped_need",
      min: -9999,
      max: 9999,
    },
    // {
    //   label: "Tracking Date",
    //   key: "tracking_start_date",
    //   min: 0,
    //   max: 100,
    // },

    

  {
    label: "Market Turn",
    key: "market_turn",
    min: 0,
    max: 999,
  },

  
   {
    label: "Inv Aging",
    key: "stock_performance_data.inv_aging",
    min: 0,
    max: 9999,
  },

  {
    label: "Sales Aging",
    key: "stock_performance_data.sold_aging",
    min: 0,
    max: 9999,
  },

  
  ];

  const newMLFilters = [
    {
      label: "Position",
      key: "ml_prediction_data.position",
      min: -999,
      max: 999999,
    },
    {
      label: "Suggested Price",
      key: "ml_prediction_data.suggested_price",
      min: 0,
      max: 999999,
    },
    
  ];
  const negativeFilters = {
    shape: "Shape",
    color: "Color",
    clarity: "Clarity",
    cut: "Cut",
    polish: "Polish",
    sym: "Symmetry",
    flr: "Fluor",
    lab: "Lab",
    shade: "Shade",
    milky: "Milky",
    // ld_diff: "LD Reco Confidence",
    locations: "Location",	
    // repriced_filter_options: "Repriced Options",
    "stock_performance_data.needs_surplus": "Needs/Surplus",
  };


  const marketFilters = newMFilters.filter(filter =>
    !['inventory_items_count', 'sold_items_count', 'attr_types.for_sale_from', 'ytd_sales_count', 'last_ld_repriced_date', 'attr_types.diamond#report_date', 'stock_performance_data.turn', "stock_performance_data.overhang", "stock_performance_data.needs", "stock_performance_data.grouped_need", "stock_performance_data.inv_aging", "stock_performance_data.sold_aging", "stock_performance_data.current_turn"].includes(filter.key)
  );

  const inventoryFilters = newMFilters.filter(filter =>
    ['inventory_items_count', 'sold_items_count', 'attr_types.for_sale_from', 'stock_performance_data.turn', 'attr_types.diamond#report_date', "stock_performance_data.overhang", 'ytd_sales_count', 'last_ld_repriced_date', "stock_performance_data.needs", "stock_performance_data.grouped_need", "stock_performance_data.inv_aging", "stock_performance_data.sold_aging", "stock_performance_data.current_turn"].includes(filter.key)
  );

  const handleMeasurement = (key, value) => {
    setFilterState((prev) => {
      let x = [...prev];
      // we find if the prev state has any obj with id of key
      // it basically checks if the obj exist if yes then we
      // only update the values, if not then create and push the object.
      let obj = x.find((obj) => obj.id === "Measurements");
      
      // if there is no object, or if length of filtered is zero(means 1st filter is being applied)
      // then we create and push a new object to array
      
      if (!obj || x.length == 0) {
        x.push({
          id: "Measurements",
          value: {
            [key]: value,
          },
        });
      } else {
        // if the item exist, then we map through the obj to get the item and update its value.
        // TODO: need to optimize this bit as we loop twice for obj and then for this block.
        x.map((item) => {
          if (item.id == "Measurements") {
            let val = item.value;
            item.value = {
              ...val,
              [key]: value,
            };
          }
        });
      }
      return x; // return the new state array
    });
  };

  // In TopFilterDrawer.jsx

const handleSetFilter = (key, value) => {
  setFilterState((prev) => {
    let x = [...prev];
    
    // pricing_action_tags: store excluded items so blue chip = included in results
    if (key === "pricing_action_tags") {
      const allOptions = pricingActionTags.map((t) => t.tag);
      // value = currently selected items from tableFilter2 standard path
      // excluded = what we store = allOptions - selected
      const excluded = allOptions.filter((opt) => !value.includes(opt));
      let obj = x.find((o) => o.id === key);
      if (!obj || x.length === 0) {
        if (value.length > 0 && excluded.length > 0) {
          x.push({ id: key, value: excluded });
        }
      } else {
        if (value.length === 0 || excluded.length === 0) {
          x = x.filter((o) => o.id !== key);
        } else {
          x = x.map((o) => o.id === key ? { ...o, value: excluded } : o);
        }
      }
      return x;
    }

    // Special handling for Needs/Surplus
    if (key === "Needs/Surplus" || key === "stock_performance_data.needs_surplus") {
      const normalizedKey = "stock_performance_data.needs_surplus";
      let obj = x.find((obj) => obj.id === normalizedKey);
      
      if (!obj || x.length === 0) {
        // First time setting this filter, just add it directly
        x.push({
          id: normalizedKey,
          value: value, // Use the selected values directly
        });
      } else {
        // Filter already exists, update it
        if (value.length === 0) {
          // Remove the filter if no values are selected
          x = x.filter((item) => item.id !== normalizedKey);
        } else {
          // Update with the selected values directly
          x = x.map((item) => item.id === normalizedKey ? { ...item, value } : item);
        }
      }
      
      return x;
    }
    
    // Standard handling for all other filters (your original code)
    let obj = x.find((obj) => obj.id === key);
    
    if (!obj || x.length == 0) {
      if (negativeFiltersList.includes(key)) {
        let a = filterKeys[negativeKeyMap[key]].filter((item) => !value.includes(item));
        x.push({
          id: key,
          value: a,
        });
      } else {
        x.push({
          id: key,
          value: value,
        });
      }
    } else {
      if (value.length == 0) {
        x = x.filter((item) => item.id !== key);
      } else {
        if (negativeFiltersList.includes(key)) {
          let a = filterKeys[negativeKeyMap[key]].filter((item) => !value.includes(item));
          x = x.map((item) => item.id === key ? { ...item, value: a } : item);
        } else {
          x = x.map((item) => item.id === key ? { ...item, value } : item);
        }
      }
    }
    
    return x;
  });
};

  function getObjectById(array, id) {
    const normalizedId = id === "Needs/Surplus" ? "stock_performance_data.needs_surplus" : id;
    let x = array.find((obj) => obj.id === normalizedId);
    return x || { id: normalizedId, value: [] };
  }

  return (
    <Drawer anchor={"right"} open={open} onClose={onClose}>
      <div className="w-[90vw] min-w-[400px] md:w-[30vw] md:min-w-[450px] md:max-w-[450px] min-h-full h-[full] flex flex-col overflow-hidden">
        <header className="min-h-[60px] border-x-0  border-t-0 border-b border-solid border-[#D8D8D8] flex items-center gap-4">
          <svg
            className="ml-4 cursor-pointer"
            xmlns="http://www.w3.org/2000/svg"
            width="15px"
            height="15px"
            viewBox="0 -2 32 32"
            onClick={onClose}
          >
            <g id="Lager_86" data-name="Lager 86" transform="translate(0 -3)">
              <path
                id="Path_94"
                data-name="Path 94"
                d="M16.587,6.42,27.167,17,16.587,27.58a2,2,0,0,0,2.833,2.833L31.414,18.42a2.013,2.013,0,0,0,0-2.84L19.42,3.587A2,2,0,0,0,16.587,6.42Z"
                fill="#161615"
              />
              <path
                id="Rectangle_45"
                data-name="Rectangle 45"
                d="M0,0H26a2,2,0,0,1,2,2V2a2,2,0,0,1-2,2H0A0,0,0,0,1,0,4V0A0,0,0,0,1,0,0Z"
                transform="translate(28 19) rotate(180)"
                fill="#161615"
              />
            </g>
          </svg>
          <h4 className="m-0 font-medium text-base">Filter Options.</h4>
        </header>

        <div className="overflow-auto h-full ">
          <div className="flex flex-col px-3 py-3 gap-2">
            <h6 className="my-0 px-2 mb-2 m-0 font-bold text-xs text-[#838383]">DIMENSIONS</h6>
            <div className="grid grid-col-1 gap-2">
              {mFilters.map((item) => {
                let obj = filtered.find((obj) => obj.id === item.label);

                return (
                  <FilterMinMaxNew
                    label={item.label}
                    setFiltered={handleSetFilter}
                    filtered={obj ? obj.value : null}
                    dMin={item.min}
                    dMax={item.max}
                    val={item.key}
                  />
                );
              })}
            </div>
          </div>
          <div className="h-[1px] w-[90%] border-x-0  border-t border-b-0 border-solid border-[#D8D8D8] m-auto" />
          <div className="flex flex-col px-3 py-3 gap-2">
            <h6 className="my-0 px-2 mb-2 m-0 font-bold text-xs text-[#838383]">
              PROPORTIONS
            </h6>
            <div className="grid grid-col-1 gap-2">
              {mMFilters.map((item) => {
                let obj = filtered.find((obj) => obj.id === item.key);

                return (
                  <FilterMinMaxNew
                    label={item.label}
                    setFiltered={handleSetFilter}
                    filtered={obj ? obj.value : null}
                    dMin={item.min}
                    dMax={item.max}
                    val={item.key}
                  />
                );
              })}
            </div>
          </div>
          <div className="h-[1px] w-[90%] border-x-0  border-t-0 border-b-0 border-solid border-[#D8D8D8] m-auto" />
          
          {Object.entries(negativeFilters).map(([key, value]) => {
            if (["shape", "lab", "shade", "locations"].includes(key.toLowerCase())) {
              return (
                <HalfExpandedCard value={["shape"].includes(key.toLowerCase()) } value1={["lab"].includes(key.toLowerCase()) }>
                  <>
                    {/* looping through all the filters */}
                    <div className="flex flex-col px-3  gap-2">
                      {/* <h6 className="m-0 font-bold text-xs text-[#838383]">LOCATION</h6> */}
                      <TableFilter2
                        data={filterKeys[key]}
                        label={value}
                        filtered={
                          filterState.length > 0
                            ? getObjectById(filterState, value)
                            : { id: value, value: [] }
                        }
                        setFiltered={handleSetFilter}
                      />
                    </div>
                  </>
                </HalfExpandedCard>
              );
            } else {
              return (
                <>
                  {/* looping through all the filters */}
                  <div className="flex flex-col px-3  gap-2">
                    {/* <h6 className="m-0 font-bold text-xs text-[#838383]">LOCATION</h6> */}
                    <TableFilter2
                      data={filterKeys[key]}
                      label={value}
                      filtered={
                        filterState.length > 0
                          ? getObjectById(filterState, value)
                          : { id: value === "Needs/Surplus" ? "stock_performance_data.needs_surplus" : value
                          , value: [] }
                      }
                      setFiltered={handleSetFilter}
                    />
                  </div>
                </>
              );
            }
          })}
          {bucketFilter && (
            <div className="flex flex-col px-3 gap-2">
              <TableFilter2
                id={bucketFilter.id}
                label={bucketFilter.label}
                data={bucketFilter.options}
                countsMap={bucketFilter.countsMap}
                filtered={getFiltered(bucketFilter)}
                setFiltered={handleSetFilter}
              />
            </div>
          )}

          {pricingActionTags.length > 0 && (
            <div className="flex flex-col px-3 gap-2">
              <TableFilter2
                id="pricing_action_tags"
                label="Price Action"
                data={pricingActionTags.map((t) => t.tag)}
                filtered={getObjectById(filterState, "pricing_action_tags")}
                setFiltered={handleSetFilter}
              />
            </div>
          )}

          <HalfExpandedCard>
            <div className="flex flex-col px-3  gap-2">
              {/* <h6 className="m-0 font-bold text-xs text-[#838383]">LOCATION</h6> */}
              <TableFilterTop
                data={filterKeys.ktsCount}
                label="KEY TO SYMBOLS"
                filtered={
                  filterState.length > 0
                    ? getObjectById(filterState, "Key To Symbol")
                    : { id: "Key To Symbol", value: [] }
                }
                setFiltered={handleSetFilter}
              />
            </div>
          </HalfExpandedCard>

          <div className="h-[1px] w-[90%] border-x-0  border-t-0 border-b border-solid border-[#D8D8D8] m-auto" />
          <HalfExpandedCard inventory={true}>
          <div className="flex flex-col px-3 py-3 gap-2">
            <h6 className="my-0 px-2 mb-2 m-0 font-bold text-xs text-[#838383]">
              INVENTORY
            </h6>
            <div className="grid grid-col-1 gap-2">
              {inventoryFilters.map((item) => {
                let obj = filtered.find((obj) => obj.id === item.key);

                return (
                  <FilterMinMaxNew
                    label={item.label}
                    setFiltered={handleSetFilter}
                    filtered={obj ? obj.value : null}
                    dMin={item.min}
                    dMax={item.max}
                    val={item.key}
                  />
                );
              })}
            </div>
          </div>
          </HalfExpandedCard>
          <div className="h-[1px] w-[90%] border-x-0  border-t-0 border-b border-solid border-[#D8D8D8] m-auto" />
          <HalfExpandedCard market={true}>
          <div className="flex flex-col px-3 py-3 gap-2">
            <h6 className="my-0 px-2 mb-2 m-0 font-bold text-xs text-[#838383]">
              MARKET
            </h6>
            <div className="grid grid-col-1 gap-2">
              {marketFilters.map((item) => {
                let obj = filtered.find((obj) => obj.id === item.key);

                return (
                  <FilterMinMaxNew
                    label={item.label}
                    setFiltered={handleSetFilter}
                    filtered={obj ? obj.value : null}
                    dMin={item.min}
                    dMax={item.max}
                    val={item.key}
                  />
                );
              })}
            </div>
          </div>
          </HalfExpandedCard>

          <div className="flex flex-col px-3 py-3 gap-2">
            <h6 className="my-0 px-2 mb-2 m-0 font-bold text-xs text-[#838383]">
              PRICING GUIDANCE
            </h6>
            <div className="grid grid-col-1 gap-2">
              {newMLFilters.map((item) => {
                let obj = filtered.find((obj) => obj.id === item.key);

                return (
                  <FilterMinMaxNew
                    label={item.label}
                    setFiltered={handleSetFilter}
                    filtered={obj ? obj.value : null}
                    dMin={item.min}
                    dMax={item.max}
                    val={item.key}
                  />
                );
              })}
            </div>
          </div>

          {mlConfidenceOptions.length > 0 && (
              <div className="flex flex-col px-3 py-2">
                
                <div className="grid grid-col-1 gap-2">
                  <TableFilter2
                    data={mlConfidenceOptions}
                    label="Confidence"
                    filtered={
                      filterState.length > 0
                        ? getObjectById(filterState, ML_CONFIDENCE_KEY)
                        : { id: ML_CONFIDENCE_KEY, value: [] }
                    }
                    setFiltered={handleSetFilter}
                  />
                </div>
                <hr className="px-3" />
              </div>
          )}

          
          
          {Array.isArray(customFiltersRaw) && customFiltersRaw.length > 0 && (
          <div className="flex flex-col px-3 py-1 gap-2">
            <h6 className="px-2 font-bold text-xs text-[#838383]">CUSTOM</h6>

            <div className="grid grid-cols-1 gap-2">
              {ordered.map(f => {
                const filteredObj = getFiltered(f);
                  return f.type === "range" ? (
                    <FilterMinMaxMemo
                      key={f.id}
                      label={f.label}
                      filtered={filteredObj ? filteredObj.value : null}
                      setFiltered={handleSetFilter}
                      dMin={f.from}
                      dMax={f.to}
                      val={f.id}
                    />

                    ) : (
                      <TableFilter2
                        key={f.id}
                        id={f.id}
                        label={f.label}
                        data={f.options}
                        countsMap={f.countsMap}
                        filtered={filteredObj}
                        setFiltered={handleSetFilter}
                      />
                  );
              })}
            </div>
          </div> )}                   
        </div>

        <div className="min-h-12 w-full  bg-white  border-x-0  border-t border-b-0 border-solid border-[#D8D8D8] flex items-center justify-evenly gap-4 px-4 py-2">
          {/* <button
            className={cn(
              "h-8 max-w-[180px] w-full border-none rounded-xl cursor-pointer text-sm font-medium",
              filterState !== filtered 
                ? "bg-[#A4D9FF]"
                : "bg-gray-200 text-gray-400"
            )}
            onClick={(e) => {
              // amplitude events.
              const buttonClickedEvent = {
                event_type: `v2_itemDetails_filter`,
                event_properties: {
                  value: filterState,
                },
              };
              amplitude.track(buttonClickedEvent);
              dispatch(setFilteredRedux(filterState));
              setFiltered(filterState);
              onClose(e);
            }}
          >
            Apply
          </button> */}
          <SaveSearchDropdown
          setFiltered={setFiltered} 
          filterState={filterState} 
          setFilteredRedux={setFilteredRedux}
          onClose={onClose} 
          filtered={filtered}
          preDefinedSearchId={preDefinedSearchId}
          sortState={sortState}
           />
          <button
            className="h-8 max-w-[180px] w-full bg-white border-[#EAEAEA] border-solid outline-none rounded-xl text-sm font-medium"
            onClick={(e) => {
              const buttonClickedEvent = {
                event_type: `v2_itemDetails_reset`,
                event_properties: {
                  value: filterState,
                },
              };
              amplitude.track(buttonClickedEvent);
              setFilterState([]);
              dispatch(setFilteredRedux([]));
              setFiltered([]);
              onClose(e);
  }}
            
          >
            Reset
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default TopFilterDrawer;